// Operations labels and small helpers: the names of the 21 pipeline steps, integrity check names, run scope, mode, acceptance status and short hashes, shared by the runs, quality, sources and system screens.
import type { CriterionStatus, RunSummary, SectionScopeOut, StepOut } from "@/api/__generated__";

export type EffectiveRunStatus = RunSummary["effective_status"];
export type Priority = SectionScopeOut["priority"];
export type StepStatus = StepOut["status"];

export const RUN_STATUSES: readonly EffectiveRunStatus[] = [
  "running",
  "interrupted",
  "succeeded",
  "partial",
  "failed",
  "rolled_back",
];

export const STEP_LABELS: Record<string, string> = {
  acquire: "Acquire the source",
  profile: "Read the layout profile",
  register: "Register the version",
  parse: "Parse the PDF",
  structure: "Build the clause tree",
  definitions: "Read definitions",
  xrefs: "Resolve cross-references",
  quality: "Measure parse quality",
  translate: "Translate to English",
  extract: "Extract requirements",
  recheck: "Re-check doubtful requirements",
  curves: "Digitise curves",
  mapping: "Map parameters",
  applicability: "Resolve applicability",
  summaries: "Summarise sections",
  write_structure: "Write the structure",
  write_requirements: "Write requirements",
  embed: "Embed for search",
  version_diff: "Compare with the previous version",
  audit: "Audit",
  finalize: "Release to readers",
};

export const STEP_STATUS_LABELS: Record<StepStatus, string> = {
  done: "Done",
  current: "In progress",
  pending: "Pending",
  not_planned: "Not run",
};

export const INTEGRITY_LABELS: Record<string, string> = {
  missing_parameter: "Requirements without a parameter",
  pu_without_base: "Per-unit values without a base",
  orphan_clauses: "Clauses without a parent",
  clauses_without_fingerprint: "Clauses without a fingerprint",
  multiple_in_force: "Families with several versions in force",
  requirements_without_reference_point: "Requirements without a reference point",
  requirements_without_applicability: "Requirements without applicability",
  in_scope_clauses_without_embedding: "In-scope clauses without an embedding",
  verdicts_without_requirement: "Verdicts without a requirement",
  requirements_without_embedding: "Requirements without an embedding",
  validity_window_conflicts: "Versions with overlapping validity",
};

export const CRITERION_LABELS: Record<CriterionStatus, string> = {
  passed: "Passed",
  failed: "Failed",
  pending: "Pending",
  not_measured: "Not measured",
};

export const EVALUATION_CRITERIA = new Set(["extraction_recall", "search_quality"]);

export const GAP_REASON_LABELS: Record<string, string> = {
  pending: "Extraction pending",
  failed: "Extraction failed",
  no_requirements: "Normative, no requirement",
};

export const PRIORITIES: readonly Priority[] = ["P1", "P2", "P3"];

export function toPriorities(values: readonly string[]): Priority[] {
  const valid = values.filter((value): value is Priority =>
    (PRIORITIES as readonly string[]).includes(value),
  );
  return valid.length > 0 ? valid : ["P1"];
}

export function stepLabel(step: string): string {
  return STEP_LABELS[step] ?? step.replaceAll("_", " ");
}

export function integrityLabel(key: string): string {
  return INTEGRITY_LABELS[key] ?? key.replaceAll("_", " ");
}

export function modeLabel(mode: string | null): string {
  if (mode === null) {
    return "–";
  }
  return mode === "sync" ? "Sync" : mode === "batch" ? "Batch" : mode;
}

export function runScope(run: Pick<RunSummary, "clause_count" | "priorities">): string {
  if (run.clause_count > 0) {
    return run.clause_count === 1 ? "1 clause" : `${String(run.clause_count)} clauses`;
  }
  return run.priorities.length > 0 ? run.priorities.join(", ") : "All";
}

export function shortHash(value: string | null, length = 12): string {
  return value === null ? "–" : value.slice(0, length);
}

export function isOpenRun(status: EffectiveRunStatus): boolean {
  return status === "running" || status === "interrupted" || status === "failed";
}
